import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Card, CardContent, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";
import GroupSelect from "../../components/GroupSelect";
import { useHostableSubjects } from "../../components/hostableSubjects";
import { tutorMeetingAppUrl } from "../../config/jitsi";

/**
 * Start class page when tutor clicks "Start class" from a course.
 * Creates a classroom and opens the in-app meeting (auto-record for hosts).
 */
export default function TutorStartClass() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  // "" = whole course; otherwise the group that alone sees this class.
  const [planId, setPlanId] = useState("");
  // The group object behind planId, so the subject picker can honour a subject-wise split.
  const [selectedGroup, setSelectedGroup] = useState(null);
  const hostableSubjects = useHostableSubjects(
    subjects,
    selectedGroup,
    subjectId,
    (id) => setSubjectId(id === "" ? "" : String(id)),
    { autoSelect: "first" }
  );
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!courseId) {
      setLoading(false);
      setError("Missing course in URL.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`tutor/courses/${encodeURIComponent(courseId)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.data) {
          setCourse(res.data.data);
        } else {
          setCourse(null);
          setError(res.data?.message || "Could not load course details.");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setCourse(null);
        setError(e.response?.data?.message || "Could not load course details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Load subjects this tutor can conduct for the course.
    setLoadingSubjects(true);
    api
      .get(`tutor/courses/${encodeURIComponent(courseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        // useHostableSubjects seeds the selection from the group-filtered list, not this one.
        setSubjects(list);
      })
      .catch(() => {
        if (cancelled) return;
        setSubjects([]);
        setSubjectId("");
      })
      .finally(() => {
        if (!cancelled) setLoadingSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const handleStartMeeting = async () => {
    if (!courseId) return;
    if (!subjectId) {
      setError("Please select a subject.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await api.post("tutor/classrooms", {
        course_id: Number(courseId),
        subject_id: Number(subjectId),
        // Omitted entirely when whole-course: null would still be a group-scoped intent to read.
        ...(planId ? { course_plan_id: Number(planId) } : {}),
        name: course ? `${course.name} - Live` : "Live class",
      });
      const code = res.data?.classroom?.room_code;
      if (code) {
        window.open(tutorMeetingAppUrl(code), "_blank", "noopener,noreferrer");
        setOpened(true);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Start class
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {course?.name ? <>Course: {course.name}. </> : null}
        Create a live meeting — it opens in a new tab. Only you (the moderator who created it) can record; students cannot.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap", mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 260 }} required>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectId}
                label="Subject"
                onChange={(e) => setSubjectId(e.target.value)}
                disabled={loadingSubjects || hostableSubjects.length === 0}
              >
                {hostableSubjects.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ minWidth: 260 }}>
              <GroupSelect
                value={planId}
                onChange={(id) => {
                  setPlanId(id);
                  // A subject the new group's tutor does not cover must not stay selected.
                  setSubjectId("");
                }}
                onGroupLoaded={setSelectedGroup}
                courseId={courseId}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<VideocamIcon />}
            onClick={handleStartMeeting}
            disabled={creating || !course || loadingSubjects || !subjectId}
            sx={{
              background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            {creating ? "Creating meeting…" : "Start meeting"}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Recording starts automatically for the host and saves to this device (or cloud/Google Drive if your Jitsi server is configured).
          </Typography>
          {opened && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Meeting opened in a new tab.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Button
        variant="outlined"
        onClick={() => navigate("/tutor/courses")}
        sx={{ mt: 2 }}
      >
        Back to My Courses
      </Button>
    </Box>
  );
}
